import { useEffect } from "react";

import {
  SELECT_DEFAULT_ITEM_HEIGHT,
  SELECT_DEFAULT_OVERSCAN,
} from "./Select.constants";
import {
  SelectOption,
  SelectProps,
  SelectVirtualizationConfig,
} from "./Select.types";

const isOptionArray = (
  value: SelectOption | SelectOption[] | null | undefined,
): value is SelectOption[] => Array.isArray(value);

export const getSelectedOptions = (
  value: SelectOption | SelectOption[] | null | undefined,
): SelectOption[] => {
  if (!value) {
    return [];
  }

  return isOptionArray(value) ? value : [value];
};

export const getCssSize = (value?: number | string): string | undefined =>
  typeof value === "number" ? `${value}px` : value;

export const getVirtualConfig = (
  virtualized: SelectProps["virtualized"],
): Required<SelectVirtualizationConfig> => {
  if (typeof virtualized === "object") {
    return {
      enabled: virtualized.enabled ?? true,
      itemHeight: virtualized.itemHeight ?? SELECT_DEFAULT_ITEM_HEIGHT,
      overscan: virtualized.overscan ?? SELECT_DEFAULT_OVERSCAN,
      threshold: virtualized.threshold ?? 80,
    };
  }

  return {
    enabled: virtualized ?? true,
    itemHeight: SELECT_DEFAULT_ITEM_HEIGHT,
    overscan: SELECT_DEFAULT_OVERSCAN,
    threshold: 80,
  };
};

export const isSameOption = (
  left: SelectOption,
  right: SelectOption,
): boolean => left.value === right.value;

export const isSelectedOption = (
  option: SelectOption,
  selectedOptions: SelectOption[],
): boolean =>
  selectedOptions.some((selected) => isSameOption(selected, option));

export const getNextMultiValue = (
  option: SelectOption,
  selectedOptions: SelectOption[],
): SelectOption[] => {
  if (isSelectedOption(option, selectedOptions)) {
    return selectedOptions.filter(
      (selected) => !isSameOption(selected, option),
    );
  }

  return [...selectedOptions, option];
};

export const useOutsideClick = (
  refs: React.RefObject<HTMLElement>[],
  enabled: boolean,
  onOutsideClick: () => void,
): void => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInside = refs.some((ref) => ref.current?.contains(target));

      if (!isInside) {
        onOutsideClick();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [enabled, onOutsideClick, refs]);
};
